import { theme } from 'antd'

/**
 * Статус листа (`SPEC-013` §4.6, §7.1).
 *
 * Копія беківського enum `Containers/LetterContainer/Database/Enum/LetterStatus.php`.
 * Значення звіряє вартовий В-6 (`Ship/Helpers/architectureGuard.test.ts`):
 * новий статус у беку без запису тут — падіння, а не сирий код у таблиці.
 *
 * Статус змінюють лише генерація, «Відправити», воркер і видалення — форма
 * його не пише (`SPEC-002` G-10).
 */
export const LETTER_STATUSES = ['draft', 'snoozed', 'sent', 'failed'] as const

export type LetterStatus = (typeof LETTER_STATUSES)[number]

/**
 * Пресетна палітра antd (сід за замовчуванням): кольори статусів — один рівень
 * насиченості (7), а не вписаний руками hex. Синій `blue6` (`#1677FF`) тут
 * не годиться: це дефолтний `colorPrimary` antd, тобто синій PD, якого в
 * темі Астерікса немає (Q14.1).
 */
const PRESET = theme.getDesignToken()

/**
 * Підписи й кольори бейджа (`SPEC-013` §9.2, Q13.10): «Відкладено» — синій,
 * «Відправлено» — зелений, «Не відправлено» — червоний. Кольори — hex із
 * пресетів, бо так їх розбирає `StatusTag` (той самий рецепт, що в колонці
 * ядра); чернетка — запасний сірий ядра.
 */
export const LETTER_STATUS_MAP: Record<LetterStatus, { label: string; color: string }> = {
    draft: { label: 'Чернетка', color: '#8C8C8C' },
    snoozed: { label: 'Відкладено', color: PRESET.blue7 },
    sent: { label: 'Відправлено', color: PRESET.green7 },
    failed: { label: 'Не відправлено', color: PRESET.red7 },
}

/**
 * Що показує вкладка «Листи» (`SPEC-013` §9.2): чернетки — ні (їх прибирає
 * чистка за добу), невідправлені — так (Q13.1: у легасі вони зникали разом
 * із чернетками).
 */
export const LETTER_TAB_STATUSES: readonly LetterStatus[] = ['snoozed', 'sent', 'failed']

/** Статуси, у яких лист ще можна змінити, відправити чи видалити (§7.1). */
export const LETTER_OPEN_STATUSES: readonly LetterStatus[] = ['draft', 'snoozed', 'failed']

export const isLetterStatus = (value: unknown): value is LetterStatus =>
    typeof value === 'string' && (LETTER_STATUSES as readonly string[]).includes(value)
